import { useRef, useState } from "react";
import { Share2, Download, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { avatarSrc } from "../../lib/api";
import { shareImage } from "../../lib/shareImage";
import { PoweredByMark } from "./PoweredBy";
import { shortDate, Btn } from "./kit";
import { Modal } from "./Form";

const STATS = [["pts", "Points"], ["reb", "Rebounds"], ["ast", "Assists"]];
const initials = (n = "") => n.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

/** Top player in points, rebounds and assists from the box score (ties go to the first listed). */
export const leadersOf = (lines, players) => STATS.map(([k, label]) => {
  const best = Object.entries(lines ?? {}).reduce((top, [id, l]) => ((l?.[k] ?? 0) > (top?.v ?? 0) ? { id, v: l[k] } : top), null);
  return { k, label, v: best?.v ?? 0, p: best ? players.find((p) => p.id === best.id) ?? { id: best.id, name: "Player" } : null };
});

const Face = ({ tid, p }) => {
  const [ok, setOk] = useState(true);
  return (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-[#39FF14]/70 bg-[#162032] font-display text-base font-black">
      {ok ? <img src={avatarSrc(tid, { ...p, has_app_photo: true })} alt="" crossOrigin="anonymous" onError={() => setOk(false)} className="h-full w-full object-cover" /> : initials(p.name)}
    </div>
  );
};

/** 360x450 card, exported at 3x = 1080x1350 to match the Starting Five graphic. */
const FinalCard = ({ cardRef, tid, teamName, game, us, them, leaders }) => {
  const won = us > them;
  return (
    <div ref={cardRef} data-testid="final-score-card" className="relative h-[450px] w-[360px] shrink-0 overflow-hidden bg-[#0A0E1A] p-6 text-white">
      <div aria-hidden className="absolute -right-4 -top-10 font-display text-[170px] font-black leading-none text-[#39FF14]/[0.06]">{won ? "W" : "L"}</div>
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-2 bg-[#39FF14]" />
      <div className="relative flex h-full flex-col">
        <p className="font-display text-sm font-bold tracking-wide">GAME<span className="text-[#FF3B30]">DAY</span> <span className="font-mono-gd text-[8px] tracking-[0.3em] text-[#39FF14]">COURTSIDE BY HOOPSHQ</span></p>
        <p className="mt-4 font-mono-gd text-[10px] uppercase tracking-[0.3em] text-[#39FF14]">Final{game.date ? ` \u00b7 ${shortDate(game.date)}` : ""}</p>
        <div className="mt-2 grid grid-cols-[1fr_auto_1fr] items-end gap-2">
          <div className="min-w-0">
            <p className="truncate font-display text-sm font-black uppercase leading-tight">{teamName}</p>
            <p data-testid="final-card-us" className={`font-mono-gd text-5xl font-bold leading-none ${won ? "text-[#39FF14]" : "text-white"}`}>{us}</p>
          </div>
          <span className={`mb-1 rounded-full px-2.5 py-0.5 font-display text-sm font-black ${won ? "bg-[#39FF14] text-[#0A0E1A]" : "bg-red-400/90 text-[#0A0E1A]"}`}>{won ? "WIN" : us === them ? "TIE" : "LOSS"}</span>
          <div className="min-w-0 text-right">
            <p className="truncate font-display text-sm font-black uppercase leading-tight">{game.opponent ?? "Opponent"}</p>
            <p data-testid="final-card-them" className={`font-mono-gd text-5xl font-bold leading-none ${!won && us !== them ? "text-[#39FF14]" : "text-white"}`}>{them}</p>
          </div>
        </div>
        <p className="mt-5 font-mono-gd text-[10px] uppercase tracking-[0.3em] text-slate-300">Game leaders</p>
        <ol className="mt-2 space-y-2">
          {leaders.map((x) => (
            <li key={x.k} data-testid={`final-leader-${x.k}`} className="flex items-center gap-3 rounded-xl bg-[#0F172A] px-3 py-1.5 ring-1 ring-[#1E293B]">
              {x.p ? <Face tid={tid} p={x.p} /> : <div className="h-12 w-12 shrink-0 rounded-full border-2 border-dashed border-[#1E293B]" />}
              <div className="min-w-0 flex-1">
                <p className="font-mono-gd text-[9px] uppercase tracking-[0.2em] text-[#39FF14]">{x.label}</p>
                <p className="truncate font-display text-lg font-black uppercase leading-none">{x.p?.name ?? "\u2014"}</p>
              </div>
              <span className="font-mono-gd text-2xl font-bold text-white">{x.p ? x.v : ""}</span>
            </li>
          ))}
        </ol>
        <div className="mt-auto pb-2 text-right"><PoweredByMark testid="final-score-powered" /></div>
      </div>
    </div>
  );
};

export const FinalScoreShare = ({ tid, teamName, game, us, them, players }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const leaders = leadersOf(game.lines, players);
  const run = async (mode) => {
    setBusy(true);
    try {
      const caption = `Final: ${teamName} ${us}, ${game.opponent ?? "Opponent"} ${them}. #GAMEDAY`;
      const how = await shareImage(ref.current, { fileName: `${(teamName ?? "team").replace(/[^a-z0-9]+/gi, "-")}-final-vs-${(game.opponent ?? "opponent").replace(/[^a-z0-9]+/gi, "-")}.png`, title: "Final score", caption, mode });
      if (how === "downloaded") toast.success(mode === "share" ? "Image downloaded. Post it from your photos" : "Image downloaded");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Couldn't create the image. Please try again");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <Btn variant="ghost" data-testid="final-score-button" onClick={() => setOpen(true)}><ImageIcon className="h-4 w-4" />Final score graphic</Btn>
      <Modal open={open} onClose={() => setOpen(false)} testid="final-score-dialog" title="Share the final score" description="Final score with the game leaders in points, rebounds and assists. Sized for Instagram, Facebook and X.">
        <div className="mt-3 flex h-[375px] justify-center overflow-hidden rounded-xl border border-[#1E293B] sm:h-[452px]">
          <div className="origin-top scale-[0.83] sm:scale-100">
            <FinalCard cardRef={ref} tid={tid} teamName={teamName} game={game} us={us} them={them} leaders={leaders} />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <Btn variant="ghost" data-testid="final-score-download" disabled={busy} onClick={() => run("download")}><Download className="h-4 w-4" />Download</Btn>
          <Btn data-testid="final-score-share" disabled={busy} onClick={() => run("share")}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}Share</Btn>
        </div>
      </Modal>
    </>
  );
};
